import type { PopulationSummary } from "../engine/aggregate"

/**
 * Level 4 — organisational results, and the Phillips fifth level on top.
 *
 * Two design decisions worth stating plainly, because they are what keep this
 * from being a number generator:
 *
 * 1. **Benefit is driven by CONFIRMED TRANSFER, not by attendance.** Every
 *    indicator moves with `kirkpatrick.behaviour / completions` — the share of
 *    completions somebody actually confirmed was applied on the job. That is
 *    Kirkpatrick's chain of evidence expressed as arithmetic: results follow
 *    behaviour, behaviour follows learning. It also means the Level 4 panel
 *    reacts to the evidence policy, which is the honest behaviour — tightening
 *    what counts as proof should change what you believe you achieved.
 *
 * 2. **The arithmetic is real even though the indicator readings are seeded.**
 *    Benefit, isolation, confidence adjustment, benefit-cost ratio and ROI are
 *    computed exactly as the ROI Methodology prescribes, from the population's
 *    own cost figure. So "how did you get that number?" has a full answer at
 *    every step, and only the baseline readings are synthetic.
 *
 * Isolation and confidence are Phillips' two guards against overclaiming: the
 * first asks how much of the improvement the programme can be credited with at
 * all, the second discounts by how sure the estimators were. Applying both is
 * what separates a defensible figure from a marketing one.
 */

export type OutcomeDirection = "down" | "up"

export interface OutcomeIndicator {
    id: string
    /** Reading before the programme. */
    baseline: number
    /** Reading in the 60–90 day results window. */
    current: number
    unit: "count" | "percent" | "days" | "score"
    /** Which direction counts as an improvement. */
    direction: OutcomeDirection
    /** Franc value of one unit of improvement. */
    usdPerUnit: number
}

export interface Level4Result {
    indicators: OutcomeIndicator[]
    /** Share of completions confirmed as applied — the driver of everything below. */
    transferRate: number
    grossBenefitUsd: number
    /** Phillips: the share of the improvement attributable to the programme. */
    isolationFactor: number
    /** Phillips: the estimators' confidence in that attribution. */
    confidence: number
    netBenefitUsd: number
    costUsd: number
    /** Benefit-cost ratio: adjusted benefit ÷ cost. */
    bcr: number
    /** ROI as a percentage: (adjusted benefit − cost) ÷ cost × 100. */
    roi: number
}

/**
 * Baseline readings, and what one unit of improvement is worth to the organisation.
 *
 * The unit values are sized for a ~2,000-person organisation spending about
 * USD 6.3 m a year on development. One avoided audit finding at USD 420k, a
 * percentage point of documentation rework at USD 2.6 m — the order of
 * magnitude a regulated enterprise of that size actually carries. Scale matters
 * here: a benefit model priced for a small programme makes any large one look
 * like a disaster, which is how an honest model produces a dishonest slide.
 *
 * At full confirmed transfer the gross benefit reaches ~USD 30 m; at the
 * population's actual transfer rate of about a third it lands near USD 9.7 m,
 * which after Phillips' two adjustments returns roughly 72 cents per dollar.
 * A NEGATIVE return is the honest reading at that transfer rate, and it is the
 * more useful demo: the lever is not more training, it is confirming that what
 * was learned gets used. Raising transfer in the manager view moves it.
 */
const INDICATORS: Array<
    Omit<OutcomeIndicator, "current"> & { maxImprovement: number }
> = [
    {
        id: "auditfeststellungen",
        baseline: 34,
        unit: "count",
        direction: "down",
        usdPerUnit: 420_000,
        maxImprovement: 12,
    },
    {
        id: "dokumentation",
        baseline: 6.8,
        unit: "percent",
        direction: "down",
        usdPerUnit: 2_600_000,
        maxImprovement: 2.4,
    },
    {
        id: "fehlerquote",
        baseline: 4.1,
        unit: "percent",
        direction: "down",
        usdPerUnit: 4_800_000,
        maxImprovement: 1.3,
    },
    {
        id: "produktivitaet",
        baseline: 96,
        unit: "days",
        direction: "down",
        usdPerUnit: 340_000,
        maxImprovement: 18,
    },
    {
        id: "servicequalitaet",
        baseline: 72,
        unit: "score",
        direction: "up",
        usdPerUnit: 730_000,
        maxImprovement: 9,
    },
]

/** Phillips' two guards. Deliberately conservative and stated on screen. */
const ISOLATION_FACTOR = 0.62
const CONFIDENCE = 0.75

export function computeLevel4(summary: PopulationSummary): Level4Result {
    const { completions, behaviour } = summary.kirkpatrick
    const transferRate = completions === 0 ? 0 : behaviour / completions

    const indicators: OutcomeIndicator[] = INDICATORS.map((indicator) => {
        const improvement = indicator.maxImprovement * transferRate
        const current =
            indicator.direction === "down"
                ? indicator.baseline - improvement
                : indicator.baseline + improvement
        return {
            id: indicator.id,
            baseline: indicator.baseline,
            current: Math.round(current * 10) / 10,
            unit: indicator.unit,
            direction: indicator.direction,
            usdPerUnit: indicator.usdPerUnit,
        }
    })

    const grossBenefitUsd = INDICATORS.reduce(
        (sum, indicator) => sum + indicator.maxImprovement * transferRate * indicator.usdPerUnit,
        0,
    )
    const netBenefitUsd = grossBenefitUsd * ISOLATION_FACTOR * CONFIDENCE
    // The programme's own cost, priced by the same rate card the ranking uses —
    // so switching to Phillips ROI changes both sides of this ratio, which is
    // exactly what it should do.
    const costUsd = Math.max(summary.totalCostUsd, 1)

    return {
        indicators,
        transferRate,
        grossBenefitUsd: Math.round(grossBenefitUsd),
        isolationFactor: ISOLATION_FACTOR,
        confidence: CONFIDENCE,
        netBenefitUsd: Math.round(netBenefitUsd),
        costUsd,
        bcr: Math.round((netBenefitUsd / costUsd) * 100) / 100,
        roi: Math.round(((netBenefitUsd - costUsd) / costUsd) * 1000) / 10,
    }
}
